import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import SelectInput from "../components/forms/SelectInput";

const disabledInputStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "0 12px",
  border: "1px solid var(--color-border, #d0d7de)",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface-muted, #f6f8fa)",
  fontSize: "var(--font-size-md)",
};

const errorTextStyle = {
  color: "var(--color-danger)",
};

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

const SettleMe = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();
  const navigate = useNavigate();

  const [excursions, setExcursions] = useState([]);
  const [selectedExcursion, setSelectedExcursion] = useState("");

  const [toReceive, setToReceive] = useState([]);
  const [toPay, setToPay] = useState([]);

  const [selectedPayerUserId, setSelectedPayerUserId] = useState("");
  const [selectedReceiverUserId, setSelectedReceiverUserId] = useState("");

  const [amount, setAmount] = useState(0);

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState("");
  const [duplicateSettlementMessage, setDuplicateSettlementMessage] =
    useState("");

  const token = localStorage.getItem("token");

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  useEffect(() => {
    const fetchExcursions = async () => {
      try {
        setLoadingPage(true);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/i-can-see`,
          { headers }
        );

        const data = Array.isArray(res.data) ? res.data : [];
        setExcursions(data);

        if (user?.excursion?.excursion_id) {
          setSelectedExcursion(String(user.excursion.excursion_id));
        }
      } catch (err) {
        console.error(err);
        setError(t("settle_me.errors.load_excursions"));
      } finally {
        setLoadingPage(false);
      }
    };

    if (user && token) fetchExcursions();
  }, [user, token, headers, t]);

  useEffect(() => {
    if (!selectedExcursion) return;

    const fetchSummary = async () => {
      try {
        setLoadingData(true);
        setError("");
        setDuplicateSettlementMessage("");

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/bills/current-user-summary/${selectedExcursion}`,
          { headers }
        );

        const receiveRows = res.data?.settlement?.to_receive || [];
        const payRows = res.data?.settlement?.to_pay || [];

        setToReceive(receiveRows);
        setToPay(payRows);

        if (receiveRows.length === 1) {
          setSelectedPayerUserId(String(receiveRows[0].from_user_id));
        } else {
          setSelectedPayerUserId("");
        }

        if (payRows.length === 1) {
          setSelectedReceiverUserId(String(payRows[0].to_user_id));
        } else {
          setSelectedReceiverUserId("");
        }
      } catch (err) {
        console.error(err);
        setError(t("settle_me.errors.load_data"));
      } finally {
        setLoadingData(false);
      }
    };

    fetchSummary();
  }, [selectedExcursion, headers, t]);

  useEffect(() => {
    const selectedReceive = toReceive.find(
      (x) => String(x.from_user_id) === String(selectedPayerUserId)
    );

    if (selectedReceive) {
      setAmount(selectedReceive.amount);
      return;
    }

    const selectedPay = toPay.find(
      (x) => String(x.to_user_id) === String(selectedReceiverUserId)
    );

    if (selectedPay) {
      setAmount(selectedPay.amount);
      return;
    }

    setAmount(0);
  }, [selectedPayerUserId, selectedReceiverUserId, toReceive, toPay]);

  const selectedExcursionData = excursions.find(
    (exc) => String(exc.excursion_id) === String(selectedExcursion)
  );

  const isExcursionLocked =
    selectedExcursionData?.status?.toLowerCase() !== "live";

  const selectedExcursionLabel = selectedExcursionData
    ? `${selectedExcursionData.short_name} (${selectedExcursionData.start_date} → ${selectedExcursionData.end_date})`
    : t("settle_me.no_excursion");

  const getSelectedReceiveItem = () => {
    return toReceive.find(
      (x) => String(x.from_user_id) === String(selectedPayerUserId)
    );
  };

  const getSelectedPayItem = () => {
    return toPay.find(
      (x) => String(x.to_user_id) === String(selectedReceiverUserId)
    );
  };

  const handleSubmit = async () => {
    const receiveItem = getSelectedReceiveItem();
    const payItem = getSelectedPayItem();

    if (!receiveItem && !payItem) return;

    if (isExcursionLocked) {
      setError(t("settle_me.errors.excursion_locked"));
      return;
    }

    setError("");
    setDuplicateSettlementMessage("");

    const currency = selectedExcursionData?.excursion_currency || "TRL";
    const numericAmount = Number(amount || 0);

    let text_translated = "";
    let payload = null;

    const today = new Date().toISOString().split("T")[0];

    if (receiveItem) {
      const fromName = receiveItem.from_name || "";
      const fromSurname = receiveItem.from_surname || "";
      const toName = user.name || "";
      const toSurname = user.surname || "";
      const visiblePerson = `${fromName} ${fromSurname}`.trim();

      text_translated = t("settle_me.definition", {
        from_name: fromName,
        from_surname: fromSurname,
        to_name: toName,
        to_surname: toSurname,
        amount: numericAmount.toFixed(2),
        currency,
        initiator_name: user.name,
        initiator_surname: user.surname,
        visible_person: visiblePerson,
      });

      payload = {
        excursion_id: parseInt(selectedExcursion, 10),
        entry_date: today,
        bill_date: today,
        currency,
        amount: numericAmount,
        definition: text_translated,
        payers: [
          {
            user_id: parseInt(selectedPayerUserId, 10),
            share: numericAmount,
          },
        ],
        beneficiaries: [
          {
            user_id: user.user_id,
            unit_share: 1,
            monetary_share: numericAmount,
          },
        ],
      };
    }

    if (payItem) {
      const fromName = user.name || "";
      const fromSurname = user.surname || "";
      const toName = payItem.to_name || "";
      const toSurname = payItem.to_surname || "";
      const visiblePerson = `${toName} ${toSurname}`.trim();

      text_translated = t("settle_me.definition", {
        from_name: fromName,
        from_surname: fromSurname,
        to_name: toName,
        to_surname: toSurname,
        amount: numericAmount.toFixed(2),
        currency,
        initiator_name: user.name,
        initiator_surname: user.surname,
        visible_person: visiblePerson,
      });

      payload = {
        excursion_id: parseInt(selectedExcursion, 10),
        entry_date: today,
        bill_date: today,
        currency,
        amount: numericAmount,
        definition: text_translated,
        payers: [
          {
            user_id: user.user_id,
            share: numericAmount,
          },
        ],
        beneficiaries: [
          {
            user_id: parseInt(selectedReceiverUserId, 10),
            unit_share: 1,
            monetary_share: numericAmount,
          },
        ],
      };
    }


    const confirmed = window.confirm(text_translated);

    if (!confirmed) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/temp-bills/create-settlement-request`,
        payload,
        { headers }
      );

      alert(t("settle_me.success"));
      navigate("/my-summary");
    } catch (err) {
      console.error("Settlement request create failed:", err);
      console.error("Status:", err.response?.status);
      console.error(
        "Backend detail:",
        JSON.stringify(err.response?.data, null, 2)
      );
      alert(t("settle_me.errors.create_failed"));
    }
  };

  if (loadingUser || loadingPage) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("settle_me.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("settle_me.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("settle_me.errors.no_session")}
          </div>
        }
      />
    );
  }

  const isEligible =
    user.role === "admin" ||
    (user.role === "member" && user.subscription_status === "approved");

  if (!isEligible) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("settle_me.title")} mode="main" />}
      >
        <ScreenContent>
          <Card variant="accent">
            <strong>{t("settle_me.errors.no_access")}</strong>
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  return (
    <StandardScreen
      topNav={<TopNav title={t("settle_me.title")} mode="main" />}
      bottomAction={
        <Button
          fullWidth
          size="lg"
          onClick={handleSubmit}
          disabled={
            isExcursionLocked ||
            (!selectedPayerUserId && !selectedReceiverUserId) ||
            !amount
          }
        >
          {t("settle_me.actions.submit")}
        </Button>
      }
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="5">
            <Card title={t("settle_me.sections.excursion")}>
              <FormField>
                <input
                  type="text"
                  value={selectedExcursionLabel}
                  disabled
                  readOnly
                  style={disabledInputStyle}
                />
              </FormField>

              {isExcursionLocked ? (
                <div style={errorTextStyle}>
                  {t("settle_me.errors.excursion_locked")}
                </div>
              ) : null}
            </Card>

            <Card title={t("settle_me.sections.form")}>
              <Stack gap="4">
                <FormField label={t("settle_me.fields.from")}>
                  <SelectInput
                    value={selectedPayerUserId}
                    onChange={(e) => {
                      setSelectedPayerUserId(e.target.value);
                      setSelectedReceiverUserId("");
                      setDuplicateSettlementMessage("");
                    }}
                    disabled={
                      isExcursionLocked || loadingData || toReceive.length <= 1
                    }
                  >
                    {toReceive.length !== 1 && (
                      <option value="">Select user</option>
                    )}

                    {toReceive.map((item) => (
                      <option
                        key={item.from_user_id}
                        value={item.from_user_id}
                      >
                        {item.from_name} {item.from_surname} (
                        {item.amount.toFixed(2)})
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("settle_me.fields.to")}>
                  <SelectInput
                    value={
                      toPay.length === 0
                        ? `${user.name} ${user.surname}`
                        : selectedReceiverUserId
                    }
                    onChange={(e) => {
                      setSelectedReceiverUserId(e.target.value);
                      setSelectedPayerUserId("");
                      setDuplicateSettlementMessage("");
                    }}
                    disabled={isExcursionLocked || loadingData || toPay.length <= 1}
                  >
                    {toPay.length === 0 ? (
                      <option value={`${user.name} ${user.surname}`}>
                        {user.name} {user.surname}
                      </option>
                    ) : (
                      <>
                        {toPay.length !== 1 && (
                          <option value="">Select user</option>
                        )}

                        {toPay.map((item) => (
                          <option key={item.to_user_id} value={item.to_user_id}>
                            {item.to_name} {item.to_surname} (
                            {item.amount.toFixed(2)})
                          </option>
                        ))}
                      </>
                    )}
                  </SelectInput>
                </FormField>

                <FormField label={t("settle_me.fields.amount")}>
                  <input
                    type="text"
                    value={Number(amount || 0).toFixed(2)}
                    disabled
                    readOnly
                    style={disabledInputStyle}
                  />
                </FormField>
              </Stack>
            </Card>

            {duplicateSettlementMessage ? (
              <Card variant="accent">
                <div>
                  {duplicateSettlementMessage}{" "}
                  <Link to="/manage-bills">Click here to check it.</Link>
                </div>
              </Card>
            ) : null}

            {error ? (
              <Card variant="accent">
                <div style={errorTextStyle}>{error}</div>
              </Card>
            ) : null}
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default SettleMe;